import { AlertCircle, X, CheckCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { showApiErrors } from "../../utils/helpers";

const ReturnModal = ({ closeReturnModal, selectedItem, fetchSaleDetail }) => {
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [returnQuantity, setReturnQuantity] = useState(
    selectedItem?.quantity || ""
  );
  const [returnReason, setReturnReason] = useState("");
  const [witnessType, setWitnessType] = useState("dispenser");
  const [witnessId, setWitnessId] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [processingReturn, setProcessingReturn] = useState(null);

  // Cashier witness fields
  const [cashierName, setCashierName] = useState("");
  const [cashierPhone, setCashierPhone] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [sentToken, setSentToken] = useState(false);
  const [sendingToken, setSendingToken] = useState(false);
  const [tokenVerified, setTokenVerified] = useState(false);

  const fetchStaffList = async () => {
    setLoadingStaff(true);
    try {
      const data = await requestWithAuth(
        `/users/accounts/?paginated=false&status=Active&staff_role=dispenser`,
        "GET"
      );
      setStaffList(data?.results || data || []);
    } catch (error) {
      console.error("Error fetching staff list:", error);
      toast.error("Failed to load staff list");
    } finally {
      setLoadingStaff(false);
    }
  };

  const handleSendVerificationToken = async () => {
    if (!cashierName.trim()) {
      toast.error("Please enter cashier name");
      return;
    }
    if (!cashierPhone.trim()) {
      toast.error("Please enter cashier phone number");
      return;
    }

    setSendingToken(true);
    try {
      const body = JSON.stringify({
        name: cashierName,
        phone: cashierPhone,
      });

      await requestWithAuth(
        `/inventory/send-witness-verification/`,
        "POST",
        body,
        {
          "Content-Type": "application/json",
        }
      );

      toast.success("Verification token sent successfully!");
      setSentToken(true);
    } catch (error) {
      console.error("Error sending verification token:", error);
      showApiErrors(error);
    } finally {
      setSendingToken(false);
    }
  };

  const handleTokenVerification = async () => {
    if (!verificationToken.trim()) {
      toast.error("Please enter verification token");
      return;
    }
    if (!cashierPhone.trim()) {
      toast.error("Please enter cashier phone number");
      return;
    }

    setSendingToken(true);
    try {
      const body = JSON.stringify({
        otp: verificationToken,
        phone: cashierPhone,
      });

      await requestWithAuth(`/inventory/verify-witness-otp/`, "POST", body, {
        "Content-Type": "application/json",
      });

      toast.success("Verification token verified successfully!");
      setTokenVerified(true);
    } catch (error) {
      console.error("Error verifying verification token:", error);
      showApiErrors(error);
    } finally {
      setSendingToken(false);
    }
  };

  const validateReturnForm = () => {
    if (!returnQuantity || returnQuantity <= 0) {
      toast.error("Please enter a valid quantity");
      return false;
    }

    if (parseFloat(returnQuantity) > parseFloat(selectedItem?.quantity)) {
      toast.error("Return quantity cannot exceed original quantity");
      return false;
    }

    if (!returnReason.trim()) {
      toast.error("Please provide a reason for the return");
      return false;
    }

    if (witnessType === "dispenser" && !witnessId) {
      toast.error("Please select a witness");
      return false;
    }

    if (witnessType === "cashier") {
      if (!cashierName.trim()) {
        toast.error("Please enter cashier name");
        return false;
      }
      if (!cashierPhone.trim()) {
        toast.error("Please enter cashier phone number");
        return false;
      }
      if (!tokenVerified) {
        toast.error("Please verify the cashier's phone number");
        return false;
      }
    }

    return true;
  };

  const handleReturnSubmit = async () => {
    if (!validateReturnForm()) return;

    Swal.fire({
      icon: "question",
      title: "Confirm Return Request?",
      html: `
                <div class="text-left">
                    <p><strong>Drug:</strong> ${selectedItem?.drug}</p>
                    <p><strong>Quantity:</strong> ${returnQuantity} of ${selectedItem?.quantity
        }</p>
                    <p><strong>Reason:</strong> ${returnReason}</p>
                    <p><strong>Witness Type:</strong> ${witnessType === "dispenser" ? "Dispenser" : "Cashier"
        }</p>
                    ${witnessType === "cashier"
          ? `<p><strong>Cashier:</strong> ${cashierName} (${cashierPhone})</p>`
          : ""
        }
                </div>
            `,
      showDenyButton: true,
      confirmButtonText: "Yes, Submit Return",
      denyButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setProcessingReturn(selectedItem?.id);
          if (witnessType === "cashier") {
            if (!tokenVerified) {
              toast.error("Please verify the token before proceeding.");
              return;
            }
          }

          const body = JSON.stringify({
            sale_item: selectedItem?.id,
            returning_quantity: parseFloat(returnQuantity),
            return_reason: returnReason,
            witness_type: witnessType,
            witness: witnessType === "dispenser" ? witnessId : null,
            witness_name: witnessType === "cashier" ? cashierName : null,
            witness_phone: witnessType === "cashier" ? cashierPhone : null,
            otp_verified: witnessType === "cashier" ? tokenVerified : null,
            drug_id: selectedItem?.drug,
            batch_id: selectedItem?.batch,
          });


          await requestWithAuth(`/inventory/sale-item-return/`, "POST", body, {
            "Content-Type": "application/json",
          });

          Swal.fire({
            icon: "success",
            title: "Success!",
            text: "Return request submitted successfully.",
          }).then(() => {
            closeReturnModal();
            fetchSaleDetail();
          });
        } catch (error) {
          console.error("Error processing return:", error);
          showApiErrors(error);
        } finally {
          setProcessingReturn(null);
        }
      }
    });
  };

  useEffect(() => {
    fetchStaffList();
  }, []);

  // Reset cashier fields when switching witness type
  useEffect(() => {
    if (witnessType === "dispenser") {
      setCashierName("");
      setCashierPhone("");
      setVerificationToken("");
      setSentToken(false);
      setTokenVerified(false);
    } else {
      setWitnessId("");
    }
  }, [witnessType]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Process Return</h2>
            <p className="text-sm text-gray-600 mt-1">
              {selectedItem?.drug_name || selectedItem?.drug}
            </p>
          </div>
          <button
            onClick={closeReturnModal}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Return Process</p>
              <p>
                This return will require witness confirmation and admin approval
                before completion.
              </p>
            </div>
          </div>

          {/* Original Quantity */}
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600">Original Quantity Sold</p>
            <p className="text-2xl font-bold text-gray-900">
              {selectedItem?.quantity}
            </p>
          </div>

          {/* Return Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quantity to Return <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              max={selectedItem?.quantity}
              value={returnQuantity}
              onChange={(e) => setReturnQuantity(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter quantity"
            />
          </div>

          {/* Return Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Return <span className="text-red-500">*</span>
            </label>
            <textarea
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="e.g., Expired product, Wrong medication, Customer complaint, etc."
            />
          </div>

          {/* Witness Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Witness Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="dispenser"
                  checked={witnessType === "dispenser"}
                  onChange={(e) => setWitnessType(e.target.value)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Dispenser</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  value="cashier"
                  checked={witnessType === "cashier"}
                  onChange={(e) => setWitnessType(e.target.value)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Cashier</span>
              </label>
            </div>
          </div>

          {/* Dispenser Witness Selection */}
          {witnessType === "dispenser" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Dispenser Witness <span className="text-red-500">*</span>
              </label>
              {loadingStaff ? (
                <div className="flex items-center gap-2 text-gray-500 py-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  <span className="text-sm">Loading staff...</span>
                </div>
              ) : (
                <select
                  value={witnessId}
                  onChange={(e) => setWitnessId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a staff member</option>
                  {staffList.map((staff) => {
                    if (staff?.id !== activeSession?.user?.id) {
                      return (
                        <option key={staff.id} value={staff.id}>
                          {staff.full_name}
                        </option>
                      );
                    }
                  })}
                </select>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Select another staff member to witness this return
              </p>
            </div>
          )}

          {/* Cashier Witness Details */}
          {witnessType === "cashier" && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cashier Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={cashierName}
                  onChange={(e) => setCashierName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter cashier's full name"
                  disabled={sentToken}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cashier Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={cashierPhone}
                  onChange={(e) => setCashierPhone(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., +233XXXXXXXXX"
                  disabled={sentToken}
                />
              </div>

              {!sentToken ? (
                <button
                  onClick={handleSendVerificationToken}
                  disabled={
                    sendingToken || !cashierName.trim() || !cashierPhone.trim()
                  }
                  className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${sendingToken || !cashierName.trim() || !cashierPhone.trim()
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                >
                  {sendingToken ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Sending Token...
                    </span>
                  ) : (
                    "Send Verification Token"
                  )}
                </button>
              ) : (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-800">
                      Verification token sent to {cashierPhone}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter Verification Token{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={verificationToken}
                      onChange={(e) => {
                        setVerificationToken(e.target.value);
                      }}
                      disabled={tokenVerified}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter token received via SMS"
                      maxLength="6"
                    />
                    {tokenVerified && (
                      <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Token verified
                      </p>
                    )}
                  </div>
                  {!tokenVerified && (
                    <button
                      onClick={() => {
                        setSentToken(false);
                        setVerificationToken("");
                        setTokenVerified(false);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 underline"
                    >
                      Resend verification token
                    </button>
                  )}

                  {!tokenVerified && (
                    <button
                      onClick={handleTokenVerification}
                      disabled={
                        !verificationToken.trim() ||
                        sendingToken ||
                        tokenVerified
                      }
                      className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${!verificationToken.trim() ||
                        sendingToken ||
                        tokenVerified
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                        }`}
                    >
                      {sendingToken ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Verifying Token...
                        </span>
                      ) : (
                        "Verify Token"
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex gap-3">
          <button
            onClick={closeReturnModal}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleReturnSubmit}
            disabled={processingReturn}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${processingReturn
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-orange-600 text-white hover:bg-orange-700"
              }`}
          >
            {processingReturn ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </span>
            ) : (
              "Submit Return Request"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReturnModal;
